import dayjs from 'dayjs';

import { HttpService } from '@/data/http/http-service';
import { User } from '@/domain/auth/entities/user.entity';
import { AuthGateway } from '@/domain/auth/gateways/auth.gateway';
import { createNanoId } from '@/ui/utils/generate-uuid';

import { getUserInfosDtoSchema, loginDtoSchema } from './auth.dto.schema';
import {
  getSessionFromLocalStorage,
  removeSessionInLocalStorage,
  setSessionInLocalStorage,
} from './auth-session-storage';

export class ApiAuthHttpGateway implements AuthGateway {
  private http = new HttpService({
    baseURL: import.meta.env.VITE_API_CONNECT,
  });

  startSignUpProcess(): void {
    const host = import.meta.env.VITE_KEYCLOAK_HOST;
    const realms = import.meta.env.VITE_KEYCLOAK_REALMS;
    const clientId = import.meta.env.VITE_KEYCLOAK_CLIENTID;
    const redirectUri = import.meta.env.VITE_KEYCLOAK_REDIRECTURI;
    const state = createNanoId(15);
    const url =
      `${host}/` +
      `realms/${realms}/` +
      'protocol/openid-connect/registrations' +
      `?client_id=${clientId}` +
      `&redirect_uri=${redirectUri}` +
      '&response_type=code' +
      '&scope=openid' +
      `&state=${state}`;
    window.location.href = url;
  }

  startSignInProcess(): void {
    const host = import.meta.env.VITE_KEYCLOAK_HOST;
    const realms = import.meta.env.VITE_KEYCLOAK_REALMS;
    const clientId = import.meta.env.VITE_KEYCLOAK_CLIENTID;
    const redirectUri = import.meta.env.VITE_KEYCLOAK_REDIRECTURI;
    const state = createNanoId(15);
    const url =
      `${host}/` +
      `realms/${realms}/` +
      'protocol/openid-connect/auth' +
      `?client_id=${clientId}` +
      `&redirect_uri=${redirectUri}` +
      '&response_type=code' +
      '&scope=openid' +
      `&state=${state}`;
    window.location.href = url;
  }

  async validateSignInAuthCode(code: string) {
    const redirectUri = import.meta.env.VITE_KEYCLOAK_REDIRECTURI;
    const url = '/login';
    const data = { code: code, redirectUri: redirectUri };
    const options = { includeAuthorization: false };
    const response = await this.http.post(url, data, options);
    if (response.status !== 201) {
      return { authorized: false };
    }
    const result = loginDtoSchema.safeParse(response.data);
    if (result.success === false) {
      console.error(result.error);
      throw result.error;
    }
    const accessToken = result.data.access_token;
    const refreshToken = result.data.refresh_token;
    setSessionInLocalStorage({ accessToken, refreshToken });
    this.http.setAccessToken(accessToken);
    return { authorized: true };
  }

  async signOut() {
    const refreshToken = getRefreshToken();
    const url = '/logout';
    const data = { refreshToken };
    try {
      const response = await this.http.post(url, data);
      if (response.status !== 201 || response.data !== true) {
        throw 'signout error';
      }
    } finally {
      removeSessionInLocalStorage();
      this.http.setAccessToken('');
    }
  }

  checkIsSignedIn() {
    const session = getSessionFromLocalStorage();
    // TODO: check if the token is not expired
    /*
     * Note: the refresh_token should be required only if the access_token is missing or expired
     * keep in mind the PDF generation mecanism CANNOT rely on the refresh_token.
     */
    if (session?.accessToken) {
      this.http.setAccessToken(session.accessToken);
      return { connected: true };
    } else {
      return { connected: false };
    }
  }

  async getUserInfos() {
    const url = '/user-info';
    const response = await this.http.get(url);
    const result = getUserInfosDtoSchema.safeParse(response.data);
    if (result.success === false) {
      console.error(result.error);
      throw result.error;
    }
    const user: User = {
      id: result.data.id,
      email: result.data.email || '',
      username: result.data.userName || '',
      firstname: result.data.firstName || '',
      lastname: result.data.lastName || '',
      licenses: result.data.licenses
        ? result.data.licenses?.map((license) => {
            return {
              mode: license.mode,
              nbProjects: Number.parseInt(license.nbProjects.toString()),
            };
          })
        : [],
      trial: {
        isOnTrial: result.data.trial?.isOnTrial || false,
        endTime: result.data.trial?.endTimestamp
          ? dayjs(result.data.trial.endTimestamp).format('YYYY-MM-DD')
          : undefined,
      },
    };
    return user;
  }

  async setExternalInvitationCode(invitationCode: string) {
    this.http.setInvitationCode(invitationCode);
  }
}

function getRefreshToken() {
  const session = getSessionFromLocalStorage();
  if (session === null) {
    throw 'no auth session';
  }
  const { refreshToken } = session;
  return refreshToken;
}
